import React, { useRef } from "react";
import { Box, Typography, Container, Grid, Chip, useMediaQuery, useTheme } from "@mui/material";
import { motion, useInView } from "framer-motion";
import BedtimeIcon from '@mui/icons-material/Bedtime';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

const RestDaysFeature = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, threshold: 0.2 });
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };
  
  // List of benefits with icons
  const benefits = [
    {
      icon: <BedtimeIcon />,
      title: "Planned Rest",
      description: "Schedule rest days in advance to keep your streaks going during vacations or busy periods"
    },
    {
      icon: <BatteryChargingFullIcon />,
      title: "Mental Recharge",
      description: "Take a break without breaking your momentum or losing your hard-earned progress"
    },
    {
      icon: <EmojiEventsIcon />,
      title: "Streak Protection",
      description: "Your streaks stay intact even on days when life gets in the way"
    }
  ];

  return (
    <Box
      ref={ref}
      sx={{
        position: "relative",
        py: { xs: 12, md: 16 },
        overflow: "hidden",
      }}
    >
      {/* Background gradient and effects */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, #1a1a35 0%, #2d2d5f 100%)`,
          zIndex: -2,
        }}
      />
      
      {/* Animated gradient orbs in background */}
      <Box
        component={motion.div}
        animate={{
          y: ["-5%", "5%"],
          x: ["3%", "-3%"],
        }}
        transition={{
          repeat: Infinity,
          repeatType: "reverse",
          duration: 15,
          ease: "easeInOut",
        }}
        sx={{
          position: "absolute",
          width: "40vw",
          height: "40vw",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(155, 90, 242, 0.2) 0%, rgba(155, 90, 242, 0) 70%)",
          filter: "blur(60px)",
          top: "-10%",
          right: "-5%",
          zIndex: -1,
        }}
      />
      
      <Box
        component={motion.div}
        animate={{
          y: ["5%", "-5%"],
          x: ["-2%", "2%"],
        }}
        transition={{
          repeat: Infinity,
          repeatType: "reverse",
          duration: 18,
          ease: "easeInOut",
        }}
        sx={{
          position: "absolute",
          width: "30vw",
          height: "30vw",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(30, 144, 255, 0.15) 0%, rgba(30, 144, 255, 0) 70%)",
          filter: "blur(60px)",
          bottom: "-10%",
          left: "20%",
          zIndex: -1,
        }}
      />

      <Container maxWidth="lg">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <Grid container spacing={6} alignItems="center">
            {/* Left side - Content */}
            <Grid item xs={12} md={6} sx={{ order: { xs: 2, md: 1 } }}>
              <Box>
                <motion.div variants={itemVariants}>
                  <Chip
                    label="STREAK MAINTENANCE"
                    sx={{
                      backgroundColor: "rgba(155, 90, 242, 0.2)",
                      color: "#9b5af2",
                      fontWeight: 600,
                      mb: 3,
                      borderRadius: "8px",
                      px: 1.5,
                      py: 2.5,
                      "& .MuiChip-label": {
                        px: 1,
                      },
                    }}
                  />
                </motion.div>

                <motion.div variants={itemVariants}>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: "2.2rem", md: "3rem" },
                      mb: 3,
                      background: "linear-gradient(to right, #ffffff, #9b5af2)",
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      textAlign: { xs: "center", md: "left" },
                    }}
                  >
                    Keep Your Streak Alive with Rest Days
                  </Typography>
                </motion.div>

                <motion.div variants={itemVariants}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: { xs: "1.1rem", md: "1.2rem" },
                      color: "rgba(255, 255, 255, 0.8)",
                      lineHeight: 1.7,
                      mb: 4,
                      textAlign: { xs: "center", md: "left" },
                    }}
                  >
                    Life happens, and sometimes you need a break. Use your{" "}
                    <Box
                      component="span"
                      sx={{
                        color: "#9b5af2",
                        fontWeight: "bold",
                        position: "relative",
                        "&::after": {
                          content: '""',
                          position: "absolute",
                          bottom: "-2px",
                          left: 0,
                          width: "100%",
                          height: "2px",
                          background: "rgba(155, 90, 242, 0.5)",
                          borderRadius: "2px",
                        },
                      }}
                    >
                      Rest Days
                    </Box>{" "}
                    to maintain your streaks without the pressure of daily check-ins. Balance consistency with the flexibility your life demands.
                  </Typography>
                </motion.div>

                {/* Benefits */}
                <Box sx={{ mt: 4 }}>
                  {benefits.map((benefit, index) => (
                    <motion.div key={index} variants={itemVariants}>
                      <Box
                        sx={{
                          display: "flex",
                          mb: 3,
                          alignItems: "flex-start",
                          backgroundColor: "rgba(255, 255, 255, 0.05)",
                          backdropFilter: "blur(10px)",
                          borderRadius: "12px",
                          p: 2,
                          border: "1px solid rgba(255, 255, 255, 0.1)",
                          transition: "transform 0.3s ease, background-color 0.3s ease",
                          "&:hover": {
                            transform: "translateY(-5px)",
                            backgroundColor: "rgba(155, 90, 242, 0.1)",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            mr: 2,
                            mt: 0.5,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "40px",
                            height: "40px",
                            borderRadius: "10px",
                            backgroundColor: "rgba(155, 90, 242, 0.15)",
                            color: "#9b5af2",
                          }}
                        >
                          {benefit.icon}
                        </Box>
                        <Box>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              fontSize: "1.1rem",
                              mb: 0.5,
                              color: "white",
                            }}
                          >
                            {benefit.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "rgba(255, 255, 255, 0.7)",
                              lineHeight: 1.6,
                            }}
                          >
                            {benefit.description}
                          </Typography>
                        </Box>
                      </Box>
                    </motion.div>
                  ))}
                </Box>
              </Box>
            </Grid>

            {/* Right side - Phone mockup */}
            <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 2 } }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  position: "relative",
                }}
              >
                {/* Glow effect */}
                <Box
                  sx={{
                    position: "absolute",
                    width: "80%",
                    height: "50%",
                    borderRadius: "50%",
                    background: "radial-gradient(ellipse, rgba(155, 90, 242, 0.3) 0%, rgba(155, 90, 242, 0) 70%)",
                    filter: "blur(40px)",
                    zIndex: 0,
                  }}
                />

                {/* Phone frame */}
                <motion.div
                  variants={itemVariants}
                  whileHover={{ y: -10, transition: { duration: 0.3 } }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      width: { xs: "250px", md: "300px" },
                      padding: "12px",
                      backgroundColor: "#121212",
                      borderRadius: "36px",
                      boxShadow: "0 30px 60px rgba(0, 0, 0, 0.4), 0 0 40px rgba(155, 90, 242, 0.2)",
                      border: "2px solid rgba(70, 70, 100, 0.6)",
                      overflow: "hidden",
                      zIndex: 1,
                    }}
                  >
                    {/* Screen content */}
                    <Box
                      sx={{
                        borderRadius: "26px",
                        overflow: "hidden",
                        position: "relative",
                      }}
                    >
                      <Box
                        component="img"
                        src="./restday.png"
                        alt="Rest Day Feature"
                        sx={{
                          width: "100%",
                          height: "auto",
                          display: "block",
                          border: "none",
                        }}
                      />

                      {/* Overlay notification for visual interest */}


                    </Box>

                    {/* Reflection effect */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "30%",
                        background: "linear-gradient(to bottom, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0))",
                        borderTopLeftRadius: "36px",
                        borderTopRightRadius: "36px",
                        pointerEvents: "none",
                        zIndex: 2,
                      }}
                    />
                  </Box>
                </motion.div>

                {/* Badge element showing number of rest days */}
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={isInView ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }}
                  transition={{ delay: 0.8, duration: 0.5, type: "spring" }}
                >

                </motion.div>
              </Box>
            </Grid>
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
};

export default RestDaysFeature;